import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MetricsResponse } from '@metron/shared';
import { useApiClient } from './useApiClient.ts';
import { toErrorMessage } from '@metron/shared';

interface UseMetricsResult {
  metrics: MetricsResponse | null;
  isLoading: boolean;
  error: string | null;
  isRefreshing: boolean;
  refresh: () => Promise<void>;
  refetch: () => void;
}

const LIVE_METRICS_QUERY_KEY = ['metrics', 'live'] as const;

/**
 * Dashboard metrics. Pass `asOf` (YYYY-MM-DD, UTC day) for the point-in-time
 * view — the key includes it so switching dates never serves another date's
 * cached numbers.
 */
export function useMetrics(asOf?: string): UseMetricsResult {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = asOf ? (['metrics', asOf] as const) : LIVE_METRICS_QUERY_KEY;

  // Fetch metrics query
  const {
    data: metrics = null,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      return await apiClient.get<MetricsResponse>(
        asOf ? `metrics?asOf=${encodeURIComponent(asOf)}` : 'metrics',
      );
    },
  });

  // Force refresh mutation (live mode only — as-of data is immutable history)
  const { mutateAsync: refreshMetrics, isPending: isRefreshing } = useMutation({
    mutationFn: async () => {
      return await apiClient.post<MetricsResponse>('metrics/refresh', {});
    },
    onSuccess: (data) => {
      // Update the cache with the fresh data
      queryClient.setQueryData(LIVE_METRICS_QUERY_KEY, data);
    },
  });

  // Error handling - convert query error to string for backward compatibility
  const error = queryError ? (toErrorMessage(queryError, 'Failed to fetch metrics')) : null;

  return {
    metrics,
    isLoading,
    error,
    isRefreshing,
    refresh: refreshMetrics,
    refetch: () => queryClient.invalidateQueries({ queryKey }),
  };
}
